/**
 * Der Import der Ausstattungs- und Pruefungsliste (ADR 0003).
 *
 * Zwei Durchgaenge: erst Vorschau, dann Uebernahme. Bei 1.500 Zeilen ist das
 * kein Luxus -- eine falsch zugeordnete Spalte faellt in der Vorschau in
 * Sekunden auf und nach der Uebernahme fruehestens beim naechsten
 * Dienstabend.
 */
import { z } from "zod";
import { Id, Inventarnummer, Zeitpunkt } from "./basis.js";
/**
 * Wohin eine Spalte der Datei gehoert.
 *
 * Die Prueffelder kommen je Zeile einzeln herein und werden anschliessend je
 * Geraet zu einer Liste zusammengefasst: Die Ausstattungslisten fuehren
 * dasselbe Geraet einmal je vorgeschriebener Pruefung.
 */
export const ImportFeld = z.enum([
    "inventarnummer",
    "bezeichnung",
    "einheit_zustaendig",
    "baujahr",
    "hersteller",
    "sachnummer",
    "geraetenummer",
    "kennzeichen",
    "pruefart",
    "letzte_pruefung",
    "pruef_ergebnis",
    "faellig",
    /** Spalte wird nicht uebernommen. */
    "ignorieren",
]);
/** Felder, die ein Artikel aus der Liste bekommt -- ohne die Pruefangaben. */
export const IMPORT_STAMMFELDER = [
    "bezeichnung",
    "einheit_zustaendig",
    "baujahr",
    "hersteller",
    "sachnummer",
    "geraetenummer",
    "kennzeichen",
];
/**
 * Zielfeld je Spalte, in der Reihenfolge der Kopfzeile.
 *
 * Bewusst nach **Spaltennummer** und nicht nach Ueberschrift: Echte Exporte
 * fuehren Ueberschriften doppelt -- "Ergebnis" steht einmal gefuellt und
 * einmal als leere Erfassungsspalte. Eine Zuordnung nach Text koennte die
 * beiden nicht auseinanderhalten, und je nachdem, welche gewinnt, wäre das
 * Pruefergebnis da oder weg.
 */
export const SpaltenZuordnung = z.array(ImportFeld);
export const ErkannteSpalte = z.object({
    ueberschrift: z.string(),
    vorschlag: ImportFeld,
    /** Die ersten Werte dieser Spalte -- damit man die Zuordnung prueft. */
    beispiele: z.array(z.string()),
});
export const FeldAenderung = z.object({
    feld: z.string(),
    alt: z.string().nullable(),
    neu: z.string().nullable(),
});
export const ArtikelNeu = z.object({
    inventarnummer: Inventarnummer,
    bezeichnung: z.string(),
    einheit_zustaendig: z.string().nullable(),
    anzahl_pruefungen: z.number().int().nonnegative(),
});
export const ArtikelGeaendert = z.object({
    artikel_id: Id,
    inventarnummer: Inventarnummer,
    bezeichnung: z.string(),
    aenderungen: z.array(FeldAenderung),
    /**
     * Seit Vertrag 2.13 immer `false`: Die Datei ist die Wahrheit, auch fuer
     * Geraete, die in der App angelegt wurden (ADR 0030). Das Feld bleibt, damit
     * nichts bricht.
     */
    nur_ergaenzen: z.boolean(),
});
export const ArtikelFehlend = z.object({
    artikel_id: Id,
    inventarnummer: Inventarnummer,
    bezeichnung: z.string(),
});
/**
 * Was die Uebernahme tun wuerde.
 *
 * `fehlend` sind Artikel, die im Bestand stehen und nicht in der Liste. Sie
 * werden **nicht** angefasst -- sie automatisch zu archivieren waere eine
 * Zustandsaenderung ohne Buchung. Sie stehen hier, damit jemand entscheiden
 * kann.
 */
export const ImportVorschau = z.object({
    id: Id,
    dateiname: z.string(),
    kodierung: z.string(),
    trennzeichen: z.string(),
    spalten: z.array(ErkannteSpalte),
    zuordnung: SpaltenZuordnung,
    zeilen_gesamt: z.number().int().nonnegative(),
    zeilen_ohne_inventarnummer: z.number().int().nonnegative(),
    /** Zeilen mit einer Nummer, die nicht dem THW-Muster entspricht. */
    zeilen_mit_ungueltiger_nummer: z.array(z.string()),
    neu: z.array(ArtikelNeu),
    geaendert: z.array(ArtikelGeaendert),
    fehlend: z.array(ArtikelFehlend),
    anzahl: z.object({
        neu: z.number().int().nonnegative(),
        geaendert: z.number().int().nonnegative(),
        unveraendert: z.number().int().nonnegative(),
        fehlend: z.number().int().nonnegative(),
    }),
});
export const ImportErgebnis = z.object({
    id: Id,
    angelegt: z.number().int().nonnegative(),
    aktualisiert: z.number().int().nonnegative(),
    unveraendert: z.number().int().nonnegative(),
    /** Nicht angefasst, nur festgehalten. */
    nicht_in_der_liste: z.number().int().nonnegative(),
    /** Davon auf Wunsch archiviert, per Buchung (ADR 0030). Aeltere Laeufe haben es nicht. */
    archiviert: z.number().int().nonnegative().optional(),
    uebernommen_am: Zeitpunkt,
});
export const ImportLauf = z.object({
    id: Id,
    dateiname: z.string(),
    hochgeladen_am: Zeitpunkt,
    hochgeladen_von: Id,
    uebernommen_am: Zeitpunkt.nullable(),
    zuordnung: SpaltenZuordnung,
    zeilen_gesamt: z.number().int().nonnegative(),
    ergebnis: ImportErgebnis.nullable(),
});
export const UebernahmeAnfrage = z.object({
    /** Angepasste Zuordnung; fehlt sie, gilt die beim Hochladen erkannte. */
    zuordnung: SpaltenZuordnung.optional(),
    /**
     * Geraete aus „nicht mehr in der Liste“, die archiviert werden sollen
     * (ADR 0030). Der Core bucht dafuer eine Statusaenderung -- Zustand
     * entsteht nur durch Buchungen. Seit Vertrag 2.13.
     */
    archivieren: z.array(Id).max(5000).default([]),
});
//# sourceMappingURL=import.js.map